import React, { useCallback, useEffect, useMemo } from 'react'

import { addToDate } from '../../helpers'
import { months } from '../../consts'
import { IntervalTimelineChart, draftBarStyle, type TimelineBar } from './echarts/IntervalTimelineChart'

interface Resale {
  id: string
  startDate: Date
  endDate: Date
  capacity_mw: number
  term_years: number
  color: string
  stackOffsetY: number
  [key: string]: any
}

interface Deal {
  start_date: string
  start_month: number
  start_year: number
  term_years: number
  capacity_mw: number
  resales?: Resale[]
  [key: string]: any
}

interface NewResale {
  start_year: string | number
  start_month: string | number
  term_years: string | number
  capacity_mw: string | number
}

interface ResaleValidation {
  isValid: boolean
  errors: string[]
}

interface DealResalesChartProps {
  // width is kept for call-site compatibility; the ECharts chart is responsive.
  width: number
  height: number
  deal: Deal
  newResale: NewResale
  setNewResaleValidation: (validation: ResaleValidation) => void
  // When provided, the draft block is draggable: dragging its body/edges reports
  // the new whole-year start/term and whole-MW volume back so the form can update.
  onDraftChange?: (fields: { start_year: number; term_years: number; capacity_mw: number }) => void
}

// A month-granular position expressed as a fractional year, so the numeric
// timeline axis can place resales precisely (e.g. 2028-04 -> 2028.25).
const yearFraction = (d: Date) => d.getFullYear() + d.getMonth() / 12 + d.getDate() / 365

// assuming that x1 < x2 and x3 < x4, check if two intervals overlap
const intervalsOverlap = ([x1, x2]: [Date, Date], [x3, x4]: [Date, Date]) => !((x4 <= x1) || (x3 >= x2))

// Interactive editor for placing a resale on a deal's timeline. All the overlap
// / stacking / validation logic (and the setNewResaleValidation callback) is
// unchanged; only the renderer moved from @visx to the shared ECharts
// IntervalTimelineChart, whose ResizeObserver also fixes the old blank-on-scroll.
export default function DealResalesChart({
  height,
  deal,
  newResale,
  setNewResaleValidation,
  onDraftChange,
}: DealResalesChartProps) {
  const dealStartDate = useMemo(() => new Date(Date.parse(deal.start_date)), [deal.start_date])
  const dealFirstYearEnd = useMemo(() => addToDate(dealStartDate, 1), [dealStartDate])
  const dealEndDate = useMemo(() => addToDate(dealStartDate, deal.term_years, 4), [dealStartDate, deal.term_years])

  const dealResalesTermSorted = useMemo(
    () => deal.resales?.slice().sort((a, b) => b.term_years - a.term_years),
    [deal.resales],
  )

  const findResaleOverlaps = useCallback(
    (resale: { startDate: Date; endDate: Date }, otherResales?: Resale[]) => otherResales?.reduce(
      (result: Resale[], otherResale) => (
        intervalsOverlap([resale.startDate, resale.endDate], [otherResale.startDate, otherResale.endDate])
          ? [...result, otherResale]
          : result
      ), [] as Resale[]),
    [],
  )

  const getMaxOffsetY = (resales?: Resale[]) => resales?.reduce(
    (maxOffsetY: number, r) => (maxOffsetY < (r.stackOffsetY + r.capacity_mw) ? (r.stackOffsetY + r.capacity_mw) : maxOffsetY),
    0,
  ) || 0

  const dealResalesStacked = useMemo(
    () => dealResalesTermSorted?.slice(1).reduce((stacked: Resale[], resale) => {
      stacked.push({ ...resale, stackOffsetY: getMaxOffsetY(findResaleOverlaps(resale, stacked)) })
      return stacked
    }, [
      ...(dealResalesTermSorted[0] ? [{ ...dealResalesTermSorted[0], stackOffsetY: 0 }] : []),
    ] as Resale[]),
    [dealResalesTermSorted, findResaleOverlaps],
  )

  const newResaleParams = useMemo(() => {
    const startDate = new Date(+newResale.start_year, (+newResale.start_month) - 1, 1)
    const endDate = new Date((+newResale.start_year) + (+newResale.term_years), (+newResale.start_month) - 1, 1)
    const offsetY = getMaxOffsetY(findResaleOverlaps({ startDate, endDate }, dealResalesStacked))
    return { startDate, endDate, offsetY }
  }, [newResale, dealResalesStacked, findResaleOverlaps])

  const newResaleValidation = useMemo(() => {
    const { startDate, endDate, offsetY } = newResaleParams
    let isValid = true
    const errors: string[] = []

    if (startDate < dealFirstYearEnd) {
      isValid = false
      errors.push(`First year is not resellable. Resale cannot start earlier than ${months[deal.start_month]} ${deal.start_year + 1}`)
    }
    if (offsetY + (+newResale.capacity_mw) > deal.capacity_mw) {
      isValid = false
      errors.push(`This resale exceeds maximum capacity of ${deal.capacity_mw} MW. Please decrease resale volume`)
    }
    if (endDate > addToDate(new Date(deal.start_year, deal.start_month - 1, 1), deal.term_years)) {
      isValid = false
      errors.push(`Resale must not extend beyond ${months[deal.start_month]} ${deal.start_year + deal.term_years}. Adjust resale start date or shorten the term`)
    }
    return { isValid, errors }
  }, [deal.capacity_mw, deal.start_month, deal.start_year, deal.term_years, dealFirstYearEnd, newResale.capacity_mw, newResaleParams])

  useEffect(() => {
    setNewResaleValidation(newResaleValidation)
  }, [newResaleValidation, setNewResaleValidation])

  const bars: TimelineBar[] = useMemo(() => {
    const result: TimelineBar[] = []

    // Committed resales - already placed. Flat light-blue blocks (settled), tagged.
    dealResalesStacked?.forEach((r) => {
      result.push({
        xStart: yearFraction(r.startDate),
        xEnd: yearFraction(r.endDate),
        yStart: r.stackOffsetY,
        yEnd: r.stackOffsetY + r.capacity_mw,
        color: '#74a5c3',
        label: `${r.capacity_mw} MW`,
        tooltip: `<b>${r.capacity_mw} MW</b> committed &middot; ${r.startDate.getFullYear()} - ${r.endDate.getFullYear()}`,
      })
    })

    // The draft you are composing - a flat, solid block you can drag/resize. Bold
    // blue is the active draft (distinct from the lighter committed blocks); when
    // it won't fit it takes a muted-red dashed outline (a genuine, transient
    // validation error). Skipped until the start date resolves, otherwise it
    // would draw at NaN coordinates (an invisible zero-size artifact).
    const previewResolvable = +newResale.capacity_mw > 0
      && Number.isFinite(yearFraction(newResaleParams.startDate))
      && Number.isFinite(yearFraction(newResaleParams.endDate))
    if (previewResolvable) {
      const { startDate, endDate, offsetY } = newResaleParams
      const isValid = newResaleValidation.isValid
      const startYear = startDate.getFullYear()
      const endYear = endDate.getFullYear()
      result.push({
        ...draftBarStyle(isValid),
        xStart: yearFraction(startDate),
        xEnd: yearFraction(endDate),
        yStart: offsetY,
        yEnd: offsetY + (+newResale.capacity_mw),
        label: `${newResale.capacity_mw} MW`,
        topLabel: `${startYear} - ${endYear}`,
        editable: true,
        tooltip: isValid
          ? `<b>New offer</b> &middot; ${newResale.capacity_mw} MW &middot; fits`
          : `<b>New offer</b> &middot; ${newResale.capacity_mw} MW &middot; won’t fit`,
      })
    }
    return result
  }, [dealResalesStacked, newResale, newResaleParams, newResaleValidation])

  return (
    <IntervalTimelineChart
      xDomain={[yearFraction(dealStartDate), yearFraction(dealEndDate)]}
      yMax={deal.capacity_mw}
      yAxisName="MW"
      bars={bars}
      blockedRegion={{ until: yearFraction(dealFirstYearEnd), label: 'Not resellable' }}
      capacityLabel={`${deal.capacity_mw} MW capacity`}
      height={height || 340}
      onEditBar={onDraftChange && (({ xStart, xEnd, yStart, yEnd }) => {
        onDraftChange({
          start_year: Math.round(xStart),
          term_years: Math.max(1, Math.round(xEnd) - Math.round(xStart)),
          capacity_mw: Math.max(1, Math.round(yEnd - yStart)),
        })
      })}
    />
  )
}
